import React, { useEffect, useState } from 'react';
import {
  Alert,
  AlertActionLink,
  Bullseye,
  Button,
  Content,
  FileUpload,
  Form,
  FormGroup,
  FormHelperText,
  FormSelect,
  FormSelectOption,
  HelperText,
  HelperTextItem,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Spinner,
} from '@patternfly/react-core';
import { useApiFetch } from '../../../api';
import { useNotifications } from '../../../contexts/NotificationContext';
import { useDnsEndpoints } from '../../../hooks/useDnsEndpoints';
import type {
  ApiResponseActivateDnsZone,
  ApiResponseImportDnsZone,
} from '../../../types/api/ApiResponseDomainName';

interface ActivateDnsZoneModalProps {
  isOpen: boolean;
  onClose: () => void;
  domainId: number; // the domain_name id
  apex: string; // domainName.name — for the copy
  onActivated: () => void | Promise<void>; // called on success (parent refetches the domain)
}

// The placeholder option maps to no selection; FormSelect reports its value as a string, so the
// selected endpoint id is held as a string and parsed to a number for the POST body.
const NO_SELECTION = '';

// How the new zone is seeded: an empty base zone (default SOA + apex NS only) or by importing an
// exported zone file. Held as a FormSelect string value, mirroring the endpoint selection above it.
// 'google' doubles as the import `source` key the backend expects (the only supported importer today).
type ZoneSource = 'empty' | 'google';

function ActivateDnsZoneModal({ isOpen, onClose, domainId, apex, onActivated }: ActivateDnsZoneModalProps) {
  const apiFetch = useApiFetch();
  const { addNotification, setDrawerOpen } = useNotifications();
  // Fetch the endpoint catalog only while the modal is open (the hook gates on this).
  const { endpoints, loading, error, refetch } = useDnsEndpoints(isOpen);

  const [selectedId, setSelectedId] = useState<string>(NO_SELECTION);
  const [zoneSource, setZoneSource] = useState<ZoneSource>('empty');
  const [yamlContent, setYamlContent] = useState('');
  const [yamlFilename, setYamlFilename] = useState('');
  const [isReadingFile, setIsReadingFile] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Reset local state whenever the modal (re)opens (mirrors ConfigureNameserversModal).
  useEffect(() => {
    if (isOpen) {
      setSelectedId(NO_SELECTION);
      setZoneSource('empty');
      setYamlContent('');
      setYamlFilename('');
      setIsReadingFile(false);
      setSubmitError(null);
      setIsSubmitting(false);
    }
  }, [isOpen]);

  const handleClose = () => {
    if (isSubmitting) return;
    onClose();
  };

  const isImport = zoneSource === 'google';

  const canSubmit =
    !isSubmitting &&
    selectedId !== NO_SELECTION &&
    domainId > 0 &&
    endpoints.length > 0 &&
    // Importing additionally requires the (uploaded or pasted) export to be present and fully read.
    (!isImport || (yamlContent.trim() !== '' && !isReadingFile));

  const clearYaml = () => {
    setYamlFilename('');
    setYamlContent('');
  };

  // Activate an empty base zone: POST …/zone with just the chosen endpoint. Unchanged behavior.
  const activateEmptyZone = async () => {
    const response = await apiFetch(`/datacenters/domain-names/${domainId}/zone`, {
      method: 'POST',
      body: JSON.stringify({ dns_endpoint_id: Number(selectedId) }),
    });

    let body: ApiResponseActivateDnsZone | null;
    try {
      body = (await response.json()) as ApiResponseActivateDnsZone;
    } catch {
      body = null;
    }

    if (!response.ok || !body || body.status !== 'success' || body.data?.dns_endpoint_id == null) {
      const message = body?.message ?? `Request failed with status ${response.status} ${response.statusText}`.trim();
      throw new Error(message);
    }

    addNotification({
      title: 'DNS zone activated',
      srTitle: 'Success alert',
      description: `The DNS zone for ${apex} is now active.`,
      variant: 'success',
    });
  };

  // Activate by importing a Google Cloud DNS YAML export: POST …/zone/import. The import both creates
  // the zone on the chosen endpoint and writes the export's record sets (apex SOA/NS stay
  // platform-managed). It is resilient — invalid/duplicate sets are reported per-RRSet, not fatal — so
  // a 'success' can still carry skipped/failed records, surfaced as a warning-variant summary.
  const importZone = async () => {
    const response = await apiFetch(`/datacenters/domain-names/${domainId}/zone/import`, {
      method: 'POST',
      body: JSON.stringify({
        source: 'google',
        dns_endpoint_id: Number(selectedId),
        content: yamlContent,
      }),
    });

    let body: ApiResponseImportDnsZone | null;
    try {
      body = (await response.json()) as ApiResponseImportDnsZone;
    } catch {
      body = null;
    }

    if (!response.ok || !body || body.status !== 'success' || body.data?.dns_endpoint_id == null) {
      const message = body?.message ?? `Request failed with status ${response.status} ${response.statusText}`.trim();
      throw new Error(message);
    }

    const { summary } = body.data.import;
    const written = summary.created + summary.replaced;
    const notImported = summary.invalid + summary.failed;
    const parts = [`${written} record set${written === 1 ? '' : 's'} imported`];
    if (summary.skipped_exists > 0) parts.push(`${summary.skipped_exists} already present`);
    if (summary.skipped_gated > 0) parts.push(`${summary.skipped_gated} platform-managed`);
    if (notImported > 0) parts.push(`${notImported} could not be imported`);

    addNotification({
      title: notImported > 0 ? 'DNS zone imported with warnings' : 'DNS zone imported',
      srTitle: notImported > 0 ? 'Warning alert' : 'Success alert',
      description: `The DNS zone for ${apex} is now active — ${parts.join(', ')}.`,
      variant: notImported > 0 ? 'warning' : 'success',
    });
  };

  const handleActivate = async () => {
    if (!canSubmit) return;

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      if (isImport) {
        await importZone();
      } else {
        await activateEmptyZone();
      }
      setDrawerOpen(true);
      await onActivated();
      onClose();
    } catch (err) {
      // Keep the modal open and surface the backend reason inline.
      setSubmitError(err instanceof Error ? err.message : 'An unexpected error occurred.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal variant="medium" isOpen={isOpen} onClose={handleClose} aria-label="Activate DNS zone">
      <ModalHeader title="Activate DNS zone" />
      <ModalBody>
        <Alert variant="info" isInline isPlain title="What activating does" className="pf-v6-u-mb-md">
          This creates the DNS zone for {apex} on the selected nameserver endpoint so its records can be managed here.
          It does <strong>not</strong> change anything at the domain&apos;s registrar and does <strong>not</strong> by
          itself make {apex} resolve to Ancalagon — the registrar&apos;s nameservers still control that.
        </Alert>

        <Content component="p" className="pf-v6-u-mb-md">
          Use this to prepare a zone ahead of a nameserver migration, or to host DNS for a domain you manage but
          didn&apos;t register through us.
        </Content>

        {submitError !== null && (
          <Alert variant="danger" isInline title="Could not activate DNS zone" className="pf-v6-u-mb-md">
            {submitError}
          </Alert>
        )}

        <Form>
          <FormGroup label="Nameserver endpoint" isRequired fieldId="activate-dns-endpoint">
            {loading ? (
              <Bullseye>
                <Spinner size="md" aria-label="Loading DNS endpoints" />
              </Bullseye>
            ) : error !== null ? (
              <Alert
                variant="danger"
                isInline
                title="Couldn't load DNS endpoints"
                actionLinks={<AlertActionLink onClick={() => void refetch()}>Retry</AlertActionLink>}
              >
                {error}
              </Alert>
            ) : endpoints.length === 0 ? (
              <Alert variant="info" isInline isPlain title="No DNS endpoints available">
                No DNS endpoints are available for this domain.
              </Alert>
            ) : (
              <FormSelect
                id="activate-dns-endpoint"
                value={selectedId}
                onChange={(_event, value) => setSelectedId(value)}
                isDisabled={isSubmitting}
              >
                <FormSelectOption value={NO_SELECTION} label="Select an endpoint" isPlaceholder />
                {endpoints.map((endpoint) => (
                  <FormSelectOption key={endpoint.id} value={String(endpoint.id)} label={endpoint.name} />
                ))}
              </FormSelect>
            )}
          </FormGroup>

          <FormGroup label="Zone source" isRequired fieldId="activate-dns-source">
            <FormSelect
              id="activate-dns-source"
              value={zoneSource}
              onChange={(_event, value) => setZoneSource(value as ZoneSource)}
              isDisabled={isSubmitting}
            >
              <FormSelectOption value="empty" label="Empty base zone" />
              <FormSelectOption value="google" label="Import from Google Cloud Platform (YAML)" />
            </FormSelect>
            <FormHelperText>
              <HelperText>
                <HelperTextItem>
                  {isImport
                    ? "Upload a Google Cloud DNS YAML export. Its record sets are imported into the new zone; the apex SOA and NS records stay managed by Ancalagon and aren't imported."
                    : 'Create an empty zone seeded only with the default SOA and apex NS records.'}
                </HelperTextItem>
              </HelperText>
            </FormHelperText>
          </FormGroup>

          {isImport && (
            <FormGroup label="Zone export file" isRequired fieldId="activate-dns-yaml">
              <FileUpload
                id="activate-dns-yaml"
                type="text"
                value={yamlContent}
                filename={yamlFilename}
                filenamePlaceholder="Drag and drop a YAML file or upload one"
                browseButtonText="Upload"
                onFileInputChange={(_event, file) => setYamlFilename(file.name)}
                onDataChange={(_event, content) => setYamlContent(content)}
                onTextChange={(_event, content) => setYamlContent(content)}
                onReadStarted={() => setIsReadingFile(true)}
                onReadFinished={() => setIsReadingFile(false)}
                onClearClick={clearYaml}
                isLoading={isReadingFile}
                isDisabled={isSubmitting}
                isRequired
                aria-label="DNS zone export file contents"
              />
              <FormHelperText>
                <HelperText>
                  <HelperTextItem>
                    The multi-document YAML produced by Google Cloud DNS (e.g. <code>gcloud dns record-sets export</code>
                    ). You can also paste its contents directly.
                  </HelperTextItem>
                </HelperText>
              </FormHelperText>
            </FormGroup>
          )}
        </Form>
      </ModalBody>
      <ModalFooter>
        <Button variant="primary" onClick={handleActivate} isLoading={isSubmitting} isDisabled={!canSubmit}>
          {isImport ? 'Import zone' : 'Activate zone'}
        </Button>
        <Button variant="link" onClick={handleClose} isDisabled={isSubmitting}>
          Cancel
        </Button>
      </ModalFooter>
    </Modal>
  );
}

export default ActivateDnsZoneModal;
